'use client';

import { cn } from '@zealot/ui/lib/utils';
import * as React from 'react';

import { type ColorFormat, DEFAULT_FALLBACK, normalizeColor } from './color-picker-utils';

type ColorPickerFieldProps = {
  id?: string;
  name?: string;
  disabled?: boolean;
  ref?: React.Ref<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  'aria-invalid'?: React.AriaAttributes['aria-invalid'];
  'aria-describedby'?: string;
};

type ColorPickerContextValue = {
  color: string;
  isEmpty: boolean;
  format: ColorFormat;
  onChange: (color: string) => void;
  fieldProps: ColorPickerFieldProps;
};

const ColorPickerContext = React.createContext<ColorPickerContextValue | null>(null);

const useColorPicker = () => {
  const context = React.useContext(ColorPickerContext);

  if (!context) {
    throw new Error('ColorPicker parts must be used within a ColorPicker.');
  }

  return context;
};

type ColorPickerProps = Omit<
  React.ComponentProps<'div'>,
  'onChange' | 'defaultValue' | 'onBlur' | 'ref' | 'aria-invalid' | 'aria-describedby'
> &
  ColorPickerFieldProps & {
    value: string | null | undefined;
    onChange: (color: string) => void;
    format?: ColorFormat;
    fallbackValue?: string;
  };

const ColorPicker = ({
  value,
  onChange,
  format = 'hex',
  fallbackValue,
  id,
  name,
  disabled,
  ref,
  onBlur,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
  className,
  children,
  ...props
}: ColorPickerProps) => {
  const normalized = normalizeColor(value, format);
  const color = normalized ?? fallbackValue ?? DEFAULT_FALLBACK[format];
  const isEmpty = normalized === null && fallbackValue === undefined;
  const contextValue = React.useMemo(
    () => ({
      color,
      isEmpty,
      format,
      onChange,
      fieldProps: {
        id,
        name,
        disabled,
        ref,
        onBlur,
        'aria-invalid': ariaInvalid,
        'aria-describedby': ariaDescribedBy,
      },
    }),
    [color, isEmpty, format, onChange, id, name, disabled, ref, onBlur, ariaInvalid, ariaDescribedBy],
  );

  return (
    <ColorPickerContext.Provider value={contextValue}>
      <div
        data-slot="color-picker"
        data-format={format}
        data-disabled={disabled ? '' : undefined}
        className={cn(
          'flex w-fit flex-col gap-2 data-disabled:pointer-events-none data-disabled:opacity-50',
          className,
        )}
        {...props}
      >
        {children}
      </div>
    </ColorPickerContext.Provider>
  );
};

export type { ColorFormat, ColorPickerFieldProps, ColorPickerProps };
export { ColorPicker, ColorPickerContext, useColorPicker };
